// Après un login réussi en tenant TSLH : auto-join la ligue 152 si nécessaire,
// sélectionner la ligue, marquer la session TSLH et rediriger vers la bonne
// page selon l'état de l'équipe.
//
// Ce helper est parallèle à `joinBpmLeagueAndRedirect` (services/oidc/callback.ts)
// mais utilise l'auth interne DynastyPool (pas d'OIDC).

import type { NavigateFunction } from 'react-router-dom';
import toast from 'react-hot-toast';
import { supabase } from './supabase';
import { useLeagueStore } from './leagueStore';
import { useUserStore } from './userStore';
import { hasAcceptedLeagueRules } from './leagueRulesAcceptance';
import { getTenantAwarePath } from './tenantStore';
import { forceTslhLogout, TSLH_LEAGUE_ID } from './tenantSession';

/**
 * Post-login TSLH : vérifier la ligue 152, sélectionner la ligue,
 * activer is_tslh_session et rediriger.
 *
 * Non-bloquant : en cas d'erreur, on log et on redirige vers /auth pour
 * éviter les boucles infinies.
 */
export async function joinTslhLeagueAndRedirect(
  userId: number,
  navigate: NavigateFunction,
): Promise<void> {
  try {
    const { data: membership, error: memberError } = await supabase
      .from('leagues_users')
      .select('initial_build_complete')
      .eq('league_id', TSLH_LEAGUE_ID)
      .eq('user_id', userId)
      .maybeSingle();

    if (memberError && memberError.code !== 'PGRST116') throw memberError;

    if (!membership) {
      forceTslhLogout(
        navigate,
        'Session TSLH invalide. Reconnecte-toi avec un compte membre de la ligue TSLH.',
      );
      return;
    }

    const buildComplete = membership.initial_build_complete ?? false;

    const { data: leagueData, error: leagueError } = await supabase
      .from('leagues')
      .select('name')
      .eq('league_id', TSLH_LEAGUE_ID)
      .single();

    if (leagueError) throw leagueError;

    useLeagueStore.getState().setSelectedLeague({
      id: TSLH_LEAGUE_ID,
      name: leagueData?.name ?? 'TSLH',
    });
    useUserStore.getState().setCurrentLeagueId(TSLH_LEAGUE_ID);
    useUserStore.getState().setBpmSession(false);
    useUserStore.getState().setTslhSession(true);

    if (!buildComplete) {
      const rulesAccepted = hasAcceptedLeagueRules(TSLH_LEAGUE_ID);
      navigate(getTenantAwarePath(rulesAccepted ? '/build-my-team' : '/league-rules-onboarding'), {
        replace: true,
      });
    } else {
      navigate(getTenantAwarePath('/my-office'), { replace: true });
    }
  } catch (err) {
    console.error('[TSLH redirect] error:', err);
    toast.error('Erreur lors de la mise en place de ta ligue. Réessaie.');
    navigate(getTenantAwarePath('/auth'), { replace: true });
  }
}
